import React, { FC, memo, useContext } from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';

import styles, { BASE_FONT_SIZE } from '../pdfStyles';
import PdfLogoComponent from '../PdfComponents/PdfLogo';
import PdfFooter from '../PdfComponents/PdfFooter';
import PdfHeaderDetails from '../PdfComponents/PdfHeaderDetails';
import PdfMainDetails from '../PdfComponents/PdfMainDetails';
import PdfTableComponent from '../PdfComponents/PdfTable';
import PdfTotalBox from '../PdfComponents/PdfTotalBox';
import PdfBillingContactDetails from '../PdfComponents/PdfBillingContactDetails';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Client } from '../../../api/clients/types';

import { VouchersPdfPropsType } from './voucherHelpers';

const StatementPdf: FC<VouchersPdfPropsType> = (props: VouchersPdfPropsType) => {
    const clientsContext = useContext(ClientsContext);

    const {
        info: {
            dueDate,
            coveredBy,
            currency,
            pdfInfo: {
                userName,
                showIssuedBy,
                voucherCertification = 'Draft',
                pdfImageSource = (clientsContext.selected as Client)?.companyLogoUrl,
                companyInfo,
                bottomDetails,
                agentInfo,
                columns,
                rows,
                summaryLine,
                columnsInvoices,
                rowsInvoices,
                pdfTotalBoxData
            }
        },
        number: voucherNumber,
        createdAt
    } = props;
    const voucherType = 'Periodic Invoice'; // document name appears as periodic invoice and not statement

    return (
        <Document title={`${voucherType} ${voucherNumber || ''}`}>
            <Page wrap size="A4" orientation="landscape" style={styles.page}>
                <PdfLogoComponent pdfImageSource={pdfImageSource} />
                <View style={styles.padding0_10}>
                    <View style={styles.section}>
                        <View style={{ ...styles.header, ...styles.spaceBetween }}>
                            <View>{agentInfo ? <PdfBillingContactDetails {...agentInfo} /> : <></>}</View>
                            <PdfHeaderDetails
                                companyVatNumber={companyInfo.vat}
                                iata={companyInfo.iata}
                                issueDate={props.issueDate || createdAt}
                                dueDate={dueDate}
                            />
                        </View>
                    </View>
                    <View style={[styles.alignItemsFlexEnd, styles.margin2_10]}>
                        <Text style={{ ...styles.button }}>{voucherCertification}</Text>
                        {coveredBy ? (
                            <Text style={{ ...styles.littleText }}>
                                {coveredBy.type} No. {coveredBy.number}
                            </Text>
                        ) : (
                            <></>
                        )}
                    </View>
                    <View style={{ ...styles.section, ...styles.alignCenter }} wrap={false}>
                        <PdfMainDetails
                            topDetails={`${voucherType} No. ${voucherNumber || 'No Document Number'}`}
                            bottomDetails={bottomDetails}
                            withForLabel
                        />
                    </View>
                    <View style={[styles.section, styles.marginBottom10px, styles.borderBottom]}>
                        <PdfTableComponent columns={columns} rows={rows} summaryLine={summaryLine} />
                    </View>
                    <PdfTotalBox pdfTotalBoxData={pdfTotalBoxData} />
                </View>
                <PdfFooter companyInfo={companyInfo} />
            </Page>
            {/* Second Page: */}
            {columnsInvoices && rowsInvoices && (
                <Page wrap size="A4" orientation="landscape" style={styles.page}>
                    <PdfLogoComponent pdfImageSource={pdfImageSource} />

                    <View style={styles.padding0_10}>
                        <View style={styles.section}>
                            <View style={{ ...styles.header, ...styles.spaceBetween }}>
                                <View>{agentInfo ? <PdfBillingContactDetails {...agentInfo} /> : <></>}</View>
                                <PdfHeaderDetails
                                    companyVatNumber={companyInfo.vat}
                                    iata={companyInfo.iata}
                                    issueDate={props.issueDate || createdAt}
                                    dueDate={dueDate}
                                />
                            </View>
                        </View>
                        <View style={[styles.alignItemsFlexEnd, styles.margin2_10]}>
                            <Text style={{ ...styles.button }}>{voucherCertification}</Text>
                        </View>

                        <View style={{ ...styles.section, ...styles.alignCenter }} wrap={false}>
                            <PdfMainDetails
                                topDetails={`${voucherType} No. ${voucherNumber || 'No Document Number'}`}
                                bottomDetails={bottomDetails}
                                withForLabel
                            />
                        </View>

                        <View style={styles.section}>
                            <Text>{`Included invoices for ${voucherType} No.:${
                                voucherNumber || 'No Document Number'
                            }`}</Text>
                            <Text>{`All amounts are in ${currency?.code || ''}`}</Text>
                            <PdfTableComponent columns={columnsInvoices} rows={rowsInvoices} />
                        </View>
                        {showIssuedBy !== false && !!userName && <Text style={{ fontSize: BASE_FONT_SIZE - 1, color: '#6b7280', marginTop: 6, marginHorizontal: 10 }}>Issued by: {userName}</Text>}
                    </View>
                    <PdfFooter companyInfo={companyInfo} />
                </Page>
            )}
        </Document>
    );
};

export default memo(StatementPdf);
